import { Box, IconButton, Typography } from '@mui/material';
import { SearchScopes } from '@zaplify/constants';
import { Loader } from '@zaplify/frontend-common';
import { apiEndpoint, getZaplifySdk } from '@zaplify/sdk';
import { Button } from '@zaplify/ui';
import { ArrowLeft, ArrowRight, Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux-latest';
import { useNavigate } from 'react-router-dom';
import {
    replaceProspectSearch,
    updateHasSomeFiltersSelectedByProspectSearchFilter,
    updateOneProspectPerCompany,
} from '../../../redux/ducks/prospect-search-filters';
import { getUnformattedProspectSearch } from '../../../redux/ducks/prospect-search-filters/helpers';
import { ProspectSearchFilterState } from '../../../redux/ducks/prospect-search-filters/types';
import { previewProspectsAction } from '../../../redux/ducks/prospect-search-result/operations';
import { updatePage } from '../../../redux/ducks/prospect-search-result/slice';
import { AppDispatch, useAppSelector } from '../../../redux/store/configureStore';
import {
    CompanyNameFilter,
    IndustriesFilter,
    KeywordsFilter,
    LocationFilter,
    OrganizationSizesFilter,
    RoleFilter,
    SummaryFilter,
    TitlesFilter,
    YearsOfExperienceFilter,
} from '../components/filter-component';
import { TableView } from './table-view/table-view';
import { useMsal } from '@azure/msal-react';
import { MarketTable } from './market-table/market-table';

export const SearchViewContainer = () => {
    const persons = useAppSelector((state) => state.prospectSearchResult.persons);
    const aggregations = useAppSelector((state) => state.prospectSearchResult.aggregations);
    const prospectSearchFilters = useAppSelector((state) => state.prospectSearchFilters);
    const hits = useAppSelector((state) => state.prospectSearchResult.hits.available);
    const loadingProspectSearch = useAppSelector((state) => state.prospectSearchResult.loadingHits);
    const loadingProspects = useAppSelector((state) => state.prospectSearchResult.loading);
    const prospectsHaveLoaded = useAppSelector((state) => state.prospectSearchResult.persons?.length > 0);
    const userId = useAppSelector((state) => state.user?.zaplifyUser?.id);
    const hasSomeFiltersSelected = useAppSelector((state) => state.prospectSearchFilters.hasSomeFiltersSelected);
    const [selectedProspectInCompanyIndex, setSelectedProspectInCompanyIndex] = useState(0);
    const msal = useMsal();

    const filterWrapper = useRef<HTMLDivElement | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [dragStartScrollLeft, setDragStartScrollLeft] = useState(0);

    const [openFilter, setOpenFilter] = useState<string | null>(null);

    const toggleFilter = (filterId: string) => {
        setOpenFilter((prev) => (prev === filterId ? null : filterId));
    };

    const navigate = useNavigate();
    const dispatch: AppDispatch = useDispatch();

    const handleScroll = () => {
        if (!isDragging && filterWrapper.current) {
            setScrollLeft(filterWrapper.current.scrollLeft);
        }
    };

    const handleArrowLeftClick = () => {
        if (filterWrapper.current) {
            const newScrollLeft = Math.max(0, scrollLeft - 100);
            filterWrapper.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
            setScrollLeft(newScrollLeft);
        }
    };

    const handleArrowRightClick = () => {
        if (filterWrapper.current) {
            const newScrollLeft = Math.min(
                filterWrapper.current.scrollWidth - filterWrapper.current.clientWidth,
                scrollLeft + 100
            );
            filterWrapper.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
            setScrollLeft(newScrollLeft);
        }
    };

    const handleSearch = () => {
        dispatch(updatePage(1));
        const tempProspectSearchFilters: ProspectSearchFilterState = {
            ...prospectSearchFilters,
            oneProspectPerCompany: true,
            excludeInOrganization: false,
            searchScope: SearchScopes.search,
            hasSomeFiltersSelected: true,
            hideOwnedCrmCompanies: false,
            hideFilterPanel: false,
        };
        dispatch(previewProspectsAction({ prospectSearchFilters, navigate, companiesPerPage: 50 }));
        dispatch(updateHasSomeFiltersSelectedByProspectSearchFilter(prospectSearchFilters));
        //setDisableSearch(true);
    };

    const handleSearchClick = async () => {
        handleSearch();
    };

    async function handleGeneratedAndFormattedFilters() {
        try {
            await dispatch(updateOneProspectPerCompany(true));
            const sdk = getZaplifySdk(apiEndpoint, msal.instance).profiles;
            const filters = await sdk.prospects.getGeneratedFilters();
            const formattedFilters = getUnformattedProspectSearch(filters);
            await dispatch(replaceProspectSearch(formattedFilters));
            const tempProspectSearchFilters: ProspectSearchFilterState = {
                ...formattedFilters,
                oneProspectPerCompany: true,
                excludeInOrganization: false,
                searchScope: SearchScopes.search,
                lastFilterPointName: {
                    point: '',
                    value: '',
                },
                hasSomeFiltersSelected: true,
                hideOwnedCrmCompanies: false,
                hideFilterPanel: false,
                yearsOfExperience: { min: 0, max: 100 },
                personalSummary: [],
                excludePersonalSummary: [],
            };
            dispatch(
                previewProspectsAction({
                    prospectSearchFilters: tempProspectSearchFilters,
                    navigate,
                    companiesPerPage: 50,
                })
            );
        } catch (error) {
            console.log('error in initial create campaign view', error);
        }
    }

    const handleSavedSearchFilters = async (savedSearchFilters) => {
        const parsedFilters = JSON.parse(savedSearchFilters || '{}');
        if (parsedFilters) {
            dispatch(replaceProspectSearch(parsedFilters));
            const tempProspectSearchFilters: ProspectSearchFilterState = {
                ...parsedFilters,
                oneProspectPerCompany: true,
                excludeInOrganization: false,
                searchScope: SearchScopes.search,
                hasSomeFiltersSelected: true,
                hideOwnedCrmCompanies: false,
                hideFilterPanel: false,
            };
            dispatch(
                previewProspectsAction({
                    prospectSearchFilters: tempProspectSearchFilters,
                    navigate,
                    companiesPerPage: 50,
                })
            );
        }
    };

    useEffect(() => {
        if (prospectsHaveLoaded) return;
        const previousSearchFilters = localStorage.getItem(`prospectSearchFilters-userId:${userId}`);
        if (!previousSearchFilters) {
            handleGeneratedAndFormattedFilters();
            return;
        }
        handleSavedSearchFilters(previousSearchFilters);
    }, []);

    const handleMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
        if (!filterWrapper.current) return;

        setIsDragging(true);
        setStartX(e.type === 'mousedown' ? (e as React.MouseEvent).pageX : (e as React.TouchEvent).touches[0].pageX);
        setDragStartScrollLeft(filterWrapper.current.scrollLeft);
    };

    const handleMouseMove = (e: React.MouseEvent | React.TouchEvent) => {
        if (!isDragging || !filterWrapper.current) return;

        const currentX =
            e.type === 'mousemove' ? (e as React.MouseEvent).pageX : (e as React.TouchEvent).touches[0].pageX;
        const distance = currentX - startX;

        requestAnimationFrame(() => {
            filterWrapper.current.scrollLeft = dragStartScrollLeft - distance;
            setScrollLeft(filterWrapper.current.scrollLeft); // Synchronizacja scrollLeft
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseLeave = () => {
        if (isDragging) setIsDragging(false);
    };

    useEffect(() => {
        const wrapper = filterWrapper.current;
        if (wrapper) {
            wrapper.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (wrapper) {
                wrapper.removeEventListener('scroll', handleScroll);
            }
        };
    }, [isDragging]);

    const displayLeftScrollButton = scrollLeft > 0;
    const displayRightScrollButton =
        filterWrapper.current && scrollLeft + filterWrapper.current.clientWidth < filterWrapper.current.scrollWidth;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                flexDirection: 'column',
                height: '100%',
            }}
        >
            {!loadingProspects && aggregations !== null && hits > 0 && (
                <MarketTable totalCount={hits} aggregations={aggregations} />
            )}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--S-2xs, 4px)',
                    alignSelf: 'stretch',
                    width: '100%',
                }}
            >
                <Box sx={{ height: '40px', width: 0 }}></Box>
                <Box
                    sx={{
                        display: 'flex',

                        alignItems: 'center',
                        gap: '8px',
                        flex: '1 0 0',

                        '-ms-overflow-style': 'none' /* IE and Edge */,
                        scrollbarWidth: 'none',
                        maxWidth: '100%',
                        minWidth: 0,
                        '&::-webkit-scrollbar': {
                            display: 'none',
                        },
                        '& > button:last-child': {
                            marginRight: '8px',
                        },
                        padding: '4px 0',
                    }}
                >
                    <Box
                        sx={{
                            zIndex: 5,
                            width: 50,
                            background:
                                'linear-gradient(270deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 30%)',
                            justifyContent: 'flex-start',
                            display: displayLeftScrollButton ? 'flex' : 'none',
                            position: 'absolute',
                        }}
                    >
                        <IconButton onClick={handleArrowLeftClick}>
                            <ArrowLeft />
                        </IconButton>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            flex: '1 0 0',
                            overflow: 'auto',
                            '-ms-overflow-style': 'none', // IE i Edge
                            scrollbarWidth: 'none', // Firefox
                            '&::-webkit-scrollbar': {
                                display: 'none', // Chrome, Safari, Opera
                            },
                            cursor: isDragging ? 'grabbing' : 'grab',
                        }}
                        ref={filterWrapper}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseLeave}
                        onTouchStart={handleMouseDown}
                        onTouchMove={handleMouseMove}
                        onTouchEnd={handleMouseUp}
                    >
                        <Box onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
                            <RoleFilter isOpen={openFilter === 'role'} toggleOpen={() => toggleFilter('role')} />
                        </Box>
                        <Box onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
                            <TitlesFilter isOpen={openFilter === 'titles'} toggleOpen={() => toggleFilter('titles')} />
                        </Box>
                        <Box onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
                            <LocationFilter
                                isOpen={openFilter === 'location'}
                                toggleOpen={() => toggleFilter('location')}
                            />
                        </Box>
                        <Box onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
                            <YearsOfExperienceFilter
                                isOpen={openFilter === 'yearsOfExperience'}
                                toggleOpen={() => toggleFilter('yearsOfExperience')}
                            />
                        </Box>
                        <Box onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
                            <KeywordsFilter
                                isOpen={openFilter === 'keywords'}
                                toggleOpen={() => toggleFilter('keywords')}
                            />
                        </Box>
                        <Box onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
                            <CompanyNameFilter
                                isOpen={openFilter === 'companyName'}
                                toggleOpen={() => toggleFilter('companyName')}
                            />
                        </Box>
                        <Box onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
                            <OrganizationSizesFilter
                                isOpen={openFilter === 'organizationSizes'}
                                toggleOpen={() => toggleFilter('organizationSizes')}
                            />
                        </Box>
                        <Box onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
                            <IndustriesFilter
                                isOpen={openFilter === 'industries'}
                                toggleOpen={() => toggleFilter('industries')}
                            />
                        </Box>
                        <Box onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
                            <SummaryFilter
                                isOpen={openFilter === 'summary'}
                                toggleOpen={() => toggleFilter('summary')}
                            />
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: displayRightScrollButton ? 'flex' : 'none',
                            zIndex: 5,
                            width: 50,
                            background: 'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 30%)',
                            justifyContent: 'flex-end',
                            position: 'absolute',
                            right: '110px',
                        }}
                    >
                        <IconButton onClick={handleArrowRightClick}>
                            <ArrowRight />
                        </IconButton>
                    </Box>
                </Box>
                <Box
                    sx={{
                        width: '1px',
                        height: '40px',
                        background: '#E4E6EB',
                        marginRight: '8px',
                    }}
                />
                <Button
                    borderRadius={300}
                    height="40px"
                    startIcon={<Search strokeWidth={1} size={20} />}
                    classes={['mint']}
                    onClick={handleSearchClick}
                >
                    Search
                </Button>
            </Box>
            {!loadingProspects && persons?.length > 0 && <TableView rows={persons} totalCount={hits} />}
            {loadingProspects && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',

                        margin: 'auto',
                        height: '100%',
                    }}
                >
                    <Box sx={{ width: '60px', height: '60px' }}>
                        <Loader />
                    </Box>
                    <Typography variant="h5">Loading prospects..</Typography>
                    <Typography variant="subtitle2">Loading might take a few seconds</Typography>
                </Box>
            )}
            {!loadingProspectSearch && !persons?.length && !hasSomeFiltersSelected && (
                <Box
                    sx={{
                        gap: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: 500,
                        textAlign: 'center',
                        height: '100%',
                        justifyContent: 'center',
                    }}
                >
                    <Typography variant="h4">Find new prospects</Typography>
                    <Typography color="textSecondary">
                        Use the filters above to build your target group, and hit “search” to find your next customer,
                        investor, or partner.
                    </Typography>
                </Box>
            )}
            {!loadingProspectSearch && !persons?.length && hasSomeFiltersSelected && (
                <Box
                    sx={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        flexDirection: 'column',
                    }}
                >
                    <Typography variant="h3">Couldn't find any prospects</Typography>
                    <Typography className="mainText">
                        No prospects was matching your search. Try removing or adding a filter and try again.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};
